// =============================================================================
// app/src/lib/faxina.ts — a lista de trabalho do cadastro (18/10/2026).
//
// "Agora faz, pois o foco primordial é fazer vídeos e cadastrar as peças."
// — o dono, 18/10/2026. MUTIRÃO continua não haverá (08/10): esta é a lista
// peça a peça, não um dia de parar a loja.
//
// Aqui só mora função PURA: o que a tela escreve e o que ela lê do roteador. A
// conta de "quanto saiu" é do banco (faxina_familias/faxina_itens, migração
// 120) — ela não pode ser feita no cliente, e isso é trava com teste:
// `agregacaoDesligada.test.ts` fixa que a agregação do PostgREST está desligada
// nos dois composes, de propósito, porque ligá-la devolveria o faturamento
// somado a quem o dono tirou dele.
// =============================================================================
import type { FaxinaFamilia, FaxinaItem } from '@core/types';

/** O que a lista de faxina manda para o cadastro pelo ESTADO do roteador. */
export interface DaFaxina {
  /** O nome que o vendedor digitou — vai no campo do nome da peça. */
  descricao: string;
  /** O código de fabricante digitado no balcão (071). Uso INTERNO. */
  codigo: string;
  montadora: string;
  modelo: string;
  /** A chave da linha de origem: é ela que permite marcar "cadastrada" quando
   *  a peça nascer, para o item SAIR da lista. Sem ela, a lista nunca
   *  encolheria e quem a usa deixaria de confiar nela. */
  chave: string;
}

/**
 * Lê o estado do roteador sem confiar no formato — mesma régua de
 * lerEstadoDaEntrada (compraNaHora.ts): quem chama pode ter vindo de qualquer
 * lugar, inclusive de um "voltar" do navegador com estado velho.
 *
 * Vai por ESTADO e não por query string de propósito: o nome e o código da
 * peça na URL ficariam no histórico do navegador e no log do servidor.
 */
export function lerEstadoDaFaxina(estado: unknown): DaFaxina {
  const e = (estado && typeof estado === 'object' ? estado : {}) as Record<string, unknown>;
  const texto = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  return {
    descricao: texto(e.descricao),
    codigo: texto(e.codigo),
    montadora: texto(e.montadora),
    modelo: texto(e.modelo),
    chave: texto(e.chave),
  };
}

/** Veio alguma coisa de verdade? Estado vazio não deve mudar o formulário. */
export const veioDaFaxina = (d: DaFaxina): boolean => d.descricao !== '' || d.codigo !== '';

/**
 * O mesmo endereço, para o `replace` que TIRA o estado do histórico.
 *
 * Existe pelo motivo que já custou caro na compra na hora: o react-router
 * guarda o `state` no `history.state`, e ele sobrevive ao salvamento. Sem
 * limpar, quem cadastra a peça, sai da tela e volta (o "voltar" do Android, ou
 * recarregar) encontra o formulário preenchido DE NOVO com o mesmo nome — e um
 * toque em Salvar cadastra a mesma peça duas vezes.
 */
export const enderecoSemEstado = (
  local: { pathname: string; search: string; hash: string },
): string => `${local.pathname}${local.search}${local.hash}`;

/**
 * O que a tela mostra como nome do item.
 *
 * `termo` é o rótulo decorado por ops.normalizar_item (Estúdio), que junta
 * 'LAMPADA H-4' e 'lampada h4'. Quando ela não reconhece o texto, vem nulo — e
 * aí vale o que o vendedor digitou. O que NUNCA pode acontecer é a linha ficar
 * sem nome: ela é a única pista de qual peça cadastrar.
 */
export const rotuloDoItem = (i: Pick<FaxinaItem, 'termo' | 'descricao'>): string =>
  (i.termo && i.termo.trim()) || i.descricao;

/** 'LAMPADA' → 'Lâmpada' não dá: o acento se perdeu na chave, de propósito
 *  (ver faxina_chave). Então a família vai em CAIXA ALTA, que é como o balcão
 *  digita, em vez de uma capitalização que inventaria o acento errado. */
export const familiaLegivel = (familia: string): string => familia.toUpperCase();

/**
 * "16 vendas · 2 encomendas · 1 orçamento" — e cada fonte quer dizer uma coisa
 * diferente para quem decide o que cadastrar:
 *   * venda = já saiu, dinheiro na mão;
 *   * encomenda = o cliente está ESPERANDO agora;
 *   * orçamento = pediu preço e não voltou.
 * Fonte com zero não aparece: linha com três números em que dois são zero
 * esconde o que importa.
 */
export function resumoDasFontes(
  i: Pick<FaxinaItem, 'vezes_venda' | 'vezes_encomenda' | 'vezes_orcamento'>,
): string {
  const partes: string[] = [];
  const diz = (n: number, um: string, muitos: string) => {
    if (n > 0) partes.push(`${n} ${n === 1 ? um : muitos}`);
  };
  diz(i.vezes_venda, 'venda', 'vendas');
  diz(i.vezes_encomenda, 'encomenda esperando', 'encomendas esperando');
  diz(i.vezes_orcamento, 'orçamento', 'orçamentos');
  return partes.join(' · ');
}

/**
 * O aviso de "já existe parecido no catálogo".
 *
 * Não é enfeite e não é erro: em 18/10/2026 LÂMPADA tinha 11 peças cadastradas
 * e foi digitada à mão 266 vezes. Esconder isso faria o gerente cadastrar a
 * décima segunda lâmpada em vez de perguntar por que ninguém acha as onze.
 * Devolve null quando não há nada parecido — e aí a tela não desenha aviso
 * nenhum, porque "0 parecidas" é ruído em 77% das linhas.
 */
export function avisoDeParecido(
  i: Pick<FaxinaItem, 'ja_no_catalogo' | 'exemplo_catalogo'>,
): string | null {
  if (!i.ja_no_catalogo || i.ja_no_catalogo < 1) return null;
  const exemplo = i.exemplo_catalogo?.trim();
  if (i.ja_no_catalogo === 1) {
    return exemplo ? `Já existe no catálogo: ${exemplo}` : 'Já existe 1 parecida no catálogo';
  }
  return exemplo
    ? `Já existem ${i.ja_no_catalogo} parecidas, como ${exemplo}`
    : `Já existem ${i.ja_no_catalogo} parecidas no catálogo`;
}

/**
 * A família já tem peça cadastrada? É a leitura de uma linha do primeiro
 * nível: "PALHETA · 120 vezes · nenhuma cadastrada" é o pior caso e o primeiro
 * trabalho; "LAMPADA · 266 vezes · 11 cadastradas" é outro problema.
 */
export const familiaSemNada = (f: Pick<FaxinaFamilia, 'ja_no_catalogo'>): boolean =>
  !f.ja_no_catalogo || f.ja_no_catalogo < 1;
